import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface RoleTemplate {
  role: string;
  label: string;
  description: string | null;
  default_sections: string[];
  is_system: boolean;
  updated_at: string;
}

const QUERY_KEY = ['role-templates'] as const;

export function useRoles() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<RoleTemplate[]> => {
      const { data, error } = await supabase
        .from('role_templates')
        .select('*')
        .order('is_system', { ascending: false })
        .order('label', { ascending: true });
      if (error) throw error;
      return (data ?? []) as RoleTemplate[];
    },
  });
}

interface CreateRoleInput {
  role: string; // ID technique (ex: 'serveur', 'cuisinier')
  label: string; // libellé FR (ex: 'Serveur')
  description?: string | null;
  default_sections?: string[];
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateRoleInput) => {
      const { error } = await supabase.from('role_templates').insert({
        role: input.role,
        label: input.label,
        description: input.description ?? null,
        default_sections: input.default_sections ?? [],
        is_system: false,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

interface UpdateRoleInput {
  role: string;
  label?: string;
  description?: string | null;
  default_sections?: string[];
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ role, ...payload }: UpdateRoleInput) => {
      const { error } = await supabase
        .from('role_templates')
        .update(payload)
        .eq('role', role);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (role: string) => {
      // Vérifier qu'aucun user n'utilise ce rôle (sécurité)
      const { count, error: countError } = await supabase
        .from('user_profiles')
        .select('id', { count: 'exact', head: true })
        .eq('role', role);
      if (countError) throw countError;
      if ((count ?? 0) > 0) {
        throw new Error(
          `Ce rôle est utilisé par ${count} utilisateur(s). Change leur rôle d'abord.`
        );
      }

      const { error } = await supabase
        .from('role_templates')
        .delete()
        .eq('role', role);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
